import { ReactElement } from 'react';
import { useFormContext, get, RegisterOptions } from 'react-hook-form';
import clsx from 'clsx';

type InputProps = {
  label?: string;
  isDisabled?: boolean;
  actionElement?: ReactElement;
  noMarginTop?: boolean;
  type?: string;
  idname: string;
  autoComplete?: string;
  validationObject?: RegisterOptions;
  isHidden?: boolean;
  min?: string;
  max?: string;
  height?: number;
  hideBorder?: boolean;
  backgroundColor?: string;
};

/**
 * A common Input control component.
 * @param props - Input props.
 * @returns - Returns a react element for input text.
 */
export default function CustomTextArea(props: InputProps): ReactElement {
  const {
    label,
    isDisabled = false,
    actionElement,
    noMarginTop,
    idname,
    autoComplete,
    validationObject,
    isHidden,
    min,
    max,
    height,
    hideBorder,
    backgroundColor
  } = props;
  const {
    register,
    formState: { errors }
  } = useFormContext();

  return (
    <div
      style={{ display: isHidden ? 'none' : 'block' }}
      className={`relative w-full ${!noMarginTop && 'mt-4'}`}
    >
      {label && (
        <label htmlFor={idname} className="text-gray-500 dark:text-gray-500 text-sm">
          {label}
          {validationObject?.required ? ' *' : ''}
        </label>
      )}
      <div className="relative">
        <textarea
          max={max}
          min={min}
          disabled={isDisabled}
          autoComplete={autoComplete}
          id={idname}
          style={{ height, backgroundColor }}
          // eslint-disable-next-line
          {...register(idname, validationObject)}
          className={clsx(
            'w-full block py-1.5 px-3.5 text-base font-normal dark:text-gray-300 placeholder-gray-400 rounded bg-gray-50 dark:bg-[transparent] focus:dark:border-primary-300 focus-within:dark:ring-transparent',
            { 'dark:text-gray-500': isDisabled },
            { 'mt-1': !!label },
            { 'border-error-300': get(errors, idname) },
            { 'border-gray-700': !get(errors, idname) },
            { border: !hideBorder }
          )}
        />
        {actionElement}
      </div>
      <p className="text-error-500 mt-1"> {get(errors, idname)?.message}</p>
    </div>
  );
}
